import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, Chip, Note, Empty } from '../../components/finance/financeUi';
import { money } from '../../components/finance/financeFormat';
import { RubyLoader } from '../../components/RubyLoader';
import { estateAdminApi, type BizStats, type FeeSource } from '../../services/estateAdminApi';

const SOURCE_LABEL: Record<FeeSource, string> = {
  override: 'Override',
  deal: 'Deal',
  default: 'Default',
  platform: 'Platform',
};

const sourceChip = (stats: BizStats) => {
  const label = SOURCE_LABEL[stats.feeSource] + (stats.feeSource === 'override' && stats.feeUntil ? ` until ${stats.feeUntil}` : '');
  const tone = stats.feeSource === 'override' ? 'blue' : stats.feeSource === 'deal' ? 'violet' : 'stone';
  return <Chip tone={tone}>{label}</Chip>;
};

const ConnectChip: React.FC<{ status: BizStats['connect'] }> = ({ status }) => (
  <Chip tone={status === 'ready' ? 'emerald' : status === 'onboarding' ? 'amber' : 'stone'}>
    {status === 'ready' ? 'Ready' : status === 'onboarding' ? 'Onboarding' : 'Not connected'}
  </Chip>
);

const EstateBusinessesPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<BizStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let off = false;
    estateAdminApi.businesses()
      .then((r) => { if (!off) setItems(r.items || []); })
      .catch((e) => { if (!off) setError(e?.message || 'Could not load Ruby Estate businesses'); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((b) => b.businessName.toLowerCase().includes(q) || b.tenantId.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <InventoryPage icon={Users} title="Landlords" subtitle="Every Ruby Estate business — occupancy, volume and fees" crumb="Ruby Estate">
      <div className="mb-4 relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by business or id…"
          className="w-full pl-9 pr-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder-stone-400 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
        />
      </div>

      {error && <Note tone="danger">{error}</Note>}

      {loading ? (
        <RubyLoader variant="page" label="Loading landlords" />
      ) : filtered.length === 0 ? (
        <Card><Empty icon={<Users size={32} />} title="No Ruby Estate businesses yet" /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                <th className="px-4 py-2.5">Business</th>
                <th className="px-4 py-2.5">Units</th>
                <th className="px-4 py-2.5">Occupancy</th>
                <th className="px-4 py-2.5">Residents</th>
                <th className="px-4 py-2.5">Leases</th>
                <th className="px-4 py-2.5">Volume (mo)</th>
                <th className="px-4 py-2.5">Fees (mo)</th>
                <th className="px-4 py-2.5">Fee plan</th>
                <th className="px-4 py-2.5">Connect</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const occupancy = b.units > 0 ? Math.round((b.occupied / b.units) * 100) : 0;
                return (
                  <tr
                    key={b.tenantId}
                    onClick={() => navigate(`/estate-admin/businesses/${encodeURIComponent(b.tenantId)}`)}
                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium text-stone-800">{b.businessName || b.tenantId}</td>
                    <td className="px-4 py-3 text-stone-600">{b.units}</td>
                    <td className="px-4 py-3 text-stone-600">{occupancy}%</td>
                    <td className="px-4 py-3 text-stone-600">{b.residents} <span className="text-stone-400">({b.residentsWithLogin} signed in)</span></td>
                    <td className="px-4 py-3 text-stone-600">{b.activeLeases}</td>
                    <td className="px-4 py-3 text-stone-800 tabular-nums">{money(b.volumeCentsThisMonth)}</td>
                    <td className="px-4 py-3 text-stone-800 tabular-nums">{money(b.feeCentsThisMonth)}</td>
                    <td className="px-4 py-3">{sourceChip(b)}</td>
                    <td className="px-4 py-3"><ConnectChip status={b.connect} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </InventoryPage>
  );
};

export default EstateBusinessesPage;
